import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import type {PipelineDependencyId, PipelineDependencySettings} from '../../types'
import {getPipelineDependency} from '../EditPipelinePage.utils'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  ensurePipelineDraftEntry,
  getPipelineDraftEntry,
} from './EditPipelinePage.slices.utils'

export const addDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    dependencyId: string
    value?: PipelineDependencySettings | null
    branch: string
  }>
> = (state, action) => {
  const {pipelineId, dependencyId, value, branch} = action.payload

  const pipeline = getPipelineDraftEntry(state, pipelineId, branch)
  const {draft} = pipeline ?? {}
  if (
    draft != null &&
    !draft.settings.dependencies?.some(dependency =>
      getPipelineDependency(dependency, dependencyId),
    )
  ) {
    const dependency =
      value == null ? (dependencyId as PipelineDependencyId) : {[dependencyId]: value}
    draft.settings.dependencies ??= []
    draft.settings.dependencies?.push(dependency)
    pipeline!.added ??= {}
    pipeline!.added.dependencies ??= {}
    pipeline!.added.dependencies[dependencyId] = true
  }
}

export const deleteDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; dependencyId: PipelineDependencyId; branch: string}>
> = (state, action) => {
  const {pipelineId, dependencyId, branch} = action.payload
  const pipeline = ensurePipelineDraftEntry(state, pipelineId, branch)

  if (pipeline.added?.dependencies?.[dependencyId] != null) {
    const dependencies = pipeline.draft?.settings?.dependencies
    const index = dependencies?.findIndex(dependency =>
      getPipelineDependency(dependency, dependencyId),
    )
    if (dependencies && index != null && index !== -1) {
      pipeline.draft?.settings?.dependencies?.splice(index, 1)
    }
    delete pipeline.added?.dependencies?.[dependencyId]
    deleteIfEmpty(pipeline.added, 'dependencies')
    deleteIfEmpty(pipeline, 'added')
    deleteIfEmpty(pipeline.draft?.settings, 'dependencies')
  } else {
    pipeline.deleted ??= {}
    pipeline.deleted!.dependencies ??= []
    pipeline.deleted!.dependencies!.push(dependencyId)
  }
}

export const updateDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    dependencyId: PipelineDependencyId
    newDepenedencyId?: PipelineDependencyId
    value?: PipelineDependencySettings | null
    branch: string
  }>
> = (state, action) => {
  const {pipelineId, dependencyId, newDepenedencyId, value, branch} = action.payload
  const {draft} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}
  if (draft != null) {
    draft.settings.dependencies ??= []
    const index = draft.settings.dependencies.findIndex(dependency =>
      getPipelineDependency(dependency, dependencyId),
    )
    if (index === -1) {
      return
    }
    if (value) {
      draft.settings.dependencies[index] = {
        [newDepenedencyId ?? dependencyId]: value,
      }
    } else {
      draft.settings.dependencies[index] = newDepenedencyId ?? dependencyId
    }
  }
}

export const applyDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; dependencyId: PipelineDependencyId; branch: string}>
> = (state, action) => {
  const {pipelineId, dependencyId, branch} = action.payload

  if (
    getPipelineDraftEntry(state, pipelineId, branch)?.added?.dependencies?.[dependencyId] != null
  ) {
    const pipeline = getPipelineDraftEntry(state, pipelineId, branch)!
    delete pipeline.added?.dependencies?.[dependencyId]
    deleteIfEmpty(pipeline.added, 'dependencies')
    deleteIfEmpty(pipeline, 'added')
  }
}

export const restoreDependency: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; dependencyId: PipelineDependencyId; branch: string}>
> = (state, action) => {
  const {pipelineId, dependencyId, branch} = action.payload
  const {deleted} = getPipelineDraftEntry(state, pipelineId, branch) ?? {}
  if (deleted?.dependencies?.includes(dependencyId)) {
    deleted.dependencies = deleted.dependencies.filter(deletedId => deletedId !== dependencyId)
    deleteIfEmpty(deleted, 'dependencies')
    deleteIfEmpty(getPipelineDraftEntry(state, pipelineId, branch), 'deleted')
  }
}
